import axios from 'axios';

const adminUrl = process.env.VUE_APP_ADMIN_URL;

/** Authorization header with the session token. */
function authHeaders() {
  const accessToken = JSON.parse(localStorage.getItem('accessToken'));
  return { Authorization: `Bearer ${accessToken}` };
}

/**
 * Turns an axios error into the service's error shape, keeping the backend's
 * error code so the UI can pick a message (never the raw backend message).
 */
function toError(err) {
  const error = (err.response && err.response.data && err.response.data.error) || {};
  return { status: 'error', code: error.code, httpCode: err.response && err.response.status };
}

/** `multipart/form-data` with the PDF in field `file`. */
function toFormData(file) {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
}

/** Upload options. Content-Type is left to axios so it adds the boundary. */
function uploadConfig(onProgress) {
  const config = { headers: authHeaders() };
  if (onProgress) {
    config.onUploadProgress = (event) => {
      if (event.total) onProgress(Math.round((event.loaded * 100) / event.total));
    };
  }
  return config;
}

/**
 * PDF of a B2B contract (backend specs/095-b2b-contract-documents). The file
 * lives in a private bucket; it is only opened through a 5-minute signed URL
 * requested on every click, never stored.
 */
export default class ContratoDocumentService {
  /**
   * Uploads the contract's first PDF (409 if it already has one).
   * @param {string} contratoId
   * @param {File} file
   * @param {(percent: number) => void} [onProgress]
   * @returns {Promise<{status: 'ok', data: object}|{status: 'error', code?: string}>}
   */
  static async upload(contratoId, file, onProgress) {
    try {
      const response = await axios.post(
        `${adminUrl}/contratos/${contratoId}/document`,
        toFormData(file),
        uploadConfig(onProgress)
      );
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      return toError(err);
    }
  }

  /**
   * Replaces the current PDF; the previous one stays in the backend as history.
   * @param {string} contratoId
   * @param {File} file
   * @param {(percent: number) => void} [onProgress]
   */
  static async replace(contratoId, file, onProgress) {
    try {
      const response = await axios.put(
        `${adminUrl}/contratos/${contratoId}/document`,
        toFormData(file),
        uploadConfig(onProgress)
      );
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      return toError(err);
    }
  }

  /**
   * Removes the current PDF (soft delete in the backend).
   * @param {string} contratoId
   */
  static async remove(contratoId) {
    try {
      await axios.delete(`${adminUrl}/contratos/${contratoId}/document`, {
        headers: authHeaders()
      });
      return { status: 'ok' };
    } catch (err) {
      return toError(err);
    }
  }

  /**
   * Asks for a short-lived signed URL to open the PDF.
   * @param {string} contratoId
   * @returns {Promise<{status: 'ok', data: {url: string, expiresAt: string}}|{status: 'error', code?: string}>}
   */
  static async getViewUrl(contratoId) {
    try {
      const response = await axios.get(`${adminUrl}/contratos/${contratoId}/document/url`, {
        headers: authHeaders()
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      return toError(err);
    }
  }
}
